import React, { useState } from "react";
import {
  FingerPrintIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import CopyValueButton from "../components/CopyValueButton";
import SEO from "../components/SEO";
import api from "../utils/api";
import { downloadFile } from "../utils/helpers";
import { generateStandardFilename } from "../../shared/utils/fileHelpers";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";

function DNSStampGenerator() {
  const [mode, setMode] = useState("encode");
  const [protocol, setProtocol] = useState("0x02"); // DNS-over-HTTPS default
  const [address, setAddress] = useState("");
  const [hostname, setHostname] = useState("");
  const [path, setPath] = useState("/dns-query");
  const [publicKey, setPublicKey] = useState("");
  const [providerName, setProviderName] = useState("");
  const [hashes, setHashes] = useState("");
  const [bootstrap, setBootstrap] = useState("");
  const [properties, setProperties] = useState({
    dnssec: false,
    noLog: true,
    noFilter: true,
  });
  const [stampInput, setStampInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 2026-07-04 tools-wide overhaul (operator-approved), audit item #15 :
  // per-field validation errors, mirroring the DCVChecker `errors`
  // object pattern so this page matches its siblings' client-side
  // validation exposure.
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  // 2026-07-04 tools-wide overhaul (operator-approved), audit item #12 :
  // state-driven transient "Copied!" feedback - now the shared
  // success-gated hook (see src/client/hooks/useCopyFeedback.js).
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();
  const resultsRef = useScrollToResults(result);

  const protocolOptions = [
    { value: "0x00", label: "Plain DNS", port: 53 },
    { value: "0x01", label: "DNSCrypt", port: 443 },
    { value: "0x02", label: "DNS-over-HTTPS", port: 443 },
    { value: "0x03", label: "DNS-over-TLS", port: 853 },
    { value: "0x04", label: "DNS-over-QUIC", port: 853 },
    { value: "0x05", label: "Oblivious DoH Target", port: 443 },
    { value: "0x81", label: "Anonymized DNSCrypt Relay", port: null },
    { value: "0x85", label: "Oblivious DoH Relay", port: 443 },
  ];

  const protocolValue = parseInt(protocol);
  const selectedProtocol = protocolOptions.find((p) => p.value === protocol);

  // Field visibility based on protocol
  const showAddressField = protocolValue !== 0x05;
  const showHostnameField = [0x02, 0x03, 0x04, 0x05, 0x85].includes(
    protocolValue
  );
  const showPathField = [0x02, 0x05, 0x85].includes(protocolValue);
  const showDNSCryptFields = protocolValue === 0x01;
  const showHashesField = [0x02, 0x03, 0x04, 0x85].includes(protocolValue);
  const showBootstrapField = [0x02, 0x03, 0x04, 0x85].includes(protocolValue);
  const showPropertiesField = protocolValue !== 0x81;

  const fillExample = () => {
    if (protocolValue === 0x02) {
      setAddress("9.9.9.9");
      setHostname("dns.quad9.net");
      setPath("/dns-query");
    } else if (protocolValue === 0x03) {
      setAddress("1.1.1.1");
      setHostname("cloudflare-dns.com");
    } else if (protocolValue === 0x00) {
      setAddress("8.8.8.8");
    }
    setErrors({});
  };

  /**
   * Clear a single field's validation error as the customer types,
   * mirroring the DCVChecker onChange pattern (audit item #15).
   */
  const clearFieldError = (field) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  /**
   * 2026-07-04 tools-wide overhaul (operator-approved), audit item #15 :
   * client-side validation of the encode form for instant feedback ;
   * the server (routes/dnsstamp.js) applies the same rules
   * authoritatively. Messages mirror the server's error sentences so
   * the customer sees identical wording either way.
   */
  const validateEncodeForm = () => {
    const newErrors = {};

    if (showAddressField && !address.trim()) {
      newErrors.address = "An IP address is required.";
    } else if (protocolValue === 0x81 && !/:\d+$/.test(address.trim())) {
      // DNSCrypt Relay has no default port in the DNS Stamps
      // specification, so the address must carry an explicit :port.
      newErrors.address = "A port must be specified for a DNSCrypt relay.";
    }

    if (showHostnameField && !hostname.trim()) {
      newErrors.hostname = "A hostname is required.";
    }

    if (showPathField) {
      if (!path.trim()) {
        newErrors.path = "A path is required.";
      } else if (!path.trim().startsWith("/")) {
        newErrors.path = "The path must start with a forward slash (/).";
      }
    }

    if (showDNSCryptFields) {
      if (!publicKey.trim()) {
        newErrors.publicKey = "A public key is required for DNSCrypt.";
      } else if (publicKey.replace(/[^0-9a-fA-F]/g, "").length !== 64) {
        // Same 32-byte rule the server enforces ; separators (colons,
        // spaces) are stripped before counting, exactly as server-side.
        newErrors.publicKey =
          "The public key must be exactly 32 bytes (64 hexadecimal characters).";
      }
      if (!providerName.trim()) {
        newErrors.providerName = "A provider name is required for DNSCrypt.";
      }
    }

    if (showHashesField && hashes.trim()) {
      // Each non-empty line must be a 32-byte SHA-256 digest in hex ;
      // formatting separators are stripped before counting.
      const badLine = hashes
        .split("\n")
        .map((h) => h.trim())
        .filter((h) => h)
        .find((h) => h.replace(/[^0-9a-fA-F]/g, "").length !== 64);
      if (badLine !== undefined) {
        newErrors.hashes =
          "Each hash must be exactly 64 hexadecimal characters (32 bytes).";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Client-side validation of the decode form (audit item #15) : the
   * only structural requirement is the sdns:// prefix, matching the
   * server's first decode check.
   */
  const validateDecodeForm = () => {
    const newErrors = {};

    if (!stampInput.trim()) {
      newErrors.stampInput = "A DNS stamp is required.";
    } else if (!stampInput.trim().startsWith("sdns://")) {
      newErrors.stampInput = "The DNS stamp must start with sdns://.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEncodeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    // Stale copy feedback goes with the result it belonged to
    // (positional keys would otherwise light up the NEW result's row
    // inside the 2-second window).
    resetCopyStatus();

    if (!validateEncodeForm()) return;

    setLoading(true);

    try {
      const response = await api.generateDNSStamp({
        protocol: protocolValue,
        address: address.trim() || undefined,
        hostname: hostname.trim() || undefined,
        path: path.trim() || undefined,
        publicKey: publicKey.trim() || undefined,
        providerName: providerName.trim() || undefined,
        hashes: hashes.trim() || undefined,
        bootstrap: bootstrap.trim() || undefined,
        properties: showPropertiesField ? properties : undefined,
      });
      // 2026-07-04 tools-wide overhaul (operator-approved), audit item
      // #9 : the route now returns the standard {success, data} envelope,
      // so the payload lives under response.data (previously the flat
      // response body itself).
      //
      // The download-filename identifier is captured HERE, at generate
      // time, not read from the form at download-click time : the form
      // is not cleared after a successful generate, so a customer who
      // starts editing fields for their next stamp and then clicks
      // Download must still get a filename describing the stamp in the
      // file (the same capture-at-result-time reasoning as the
      // decodedCsr state in src/client/pages/CSRDecoder.jsx).
      //
      // Each field only counts while its protocol SHOWS it : switching
      // protocol hides fields without clearing them, so a stale hidden
      // hostname (typed under DoH, then protocol switched to Plain
      // DNS) must not name a file it is not encoded in - the server
      // ignores hidden fields when building the stamp
      // (routes/dnsstamp.js encodes per-protocol field sets). After
      // validateEncodeForm, every protocol has a non-empty visible
      // hostname or address (address is required except for Oblivious
      // DoH Target 0x05, which requires hostname), so "resolver" is a
      // defense-in-depth fallback rather than a reachable branch.
      const identifier =
        (showHostnameField ? hostname.trim() : "") ||
        (showAddressField ? address.trim() : "") ||
        "resolver";
      setResult({ type: "encode", data: response.data, identifier });
    } catch (err) {
      setError(err.message || "The DNS stamp could not be generated.");
    } finally {
      setLoading(false);
    }
  };

  const handleDecodeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    // Stale copy feedback goes with the result it belonged to
    // (positional keys would otherwise light up the NEW result's row
    // inside the 2-second window).
    resetCopyStatus();

    if (!validateDecodeForm()) return;

    setLoading(true);

    try {
      const stamp = stampInput.trim();
      const response = await api.decodeDNSStamp(stamp);
      // Audit item #9 : same envelope unwrap as handleEncodeSubmit above.
      //
      // The decode branch's Download saves the DECODED stamp under a
      // filename named from the decoded resolver (the CSRDecoder
      // decodedCsr idiom : both capture the artifact and its
      // identifier at result time, so later textarea edits cannot
      // desync the file from the on-screen details). The identifier
      // comes from the DECODED fields, not the form.
      const decoded = response.data?.decoded || {};
      setResult({
        type: "decode",
        data: response.data,
        stamp,
        identifier: decoded.hostname || decoded.address || "resolver",
      });
    } catch (err) {
      setError(err.message || "The DNS stamp could not be decoded.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Copy the generated stamp with transient visual feedback (audit
   * item #12) : the shared hook gates the "Copied!" confirmation on
   * the clipboard helper's success flag.
   */
  const handleCopyStamp = (stamp) => handleCopy(stamp, "stamp");

  /**
   * 2026-08-07 download/copy consistency pass (operator-directed) :
   * every tool whose result is a savable artifact offers a Download
   * button under the standard {identifier}-{type}-{timestamp} filename
   * (docs/FILENAME_STANDARDS.md). Shared by BOTH branches : the
   * identifier (resolver hostname, else address, else the "resolver"
   * fallback) is the one captured with the result - at generate time
   * on the encode branch (see the identifier comment in
   * handleEncodeSubmit for why it must not be re-read from the live
   * form) and from the DECODED fields at decode time (see
   * handleDecodeSubmit).
   */
  const handleDownloadStamp = (stamp) => {
    downloadFile(
      stamp,
      generateStandardFilename(result.identifier, "dns-stamp", "txt")
    );
  };

  const handleReset = () => {
    setAddress("");
    setHostname("");
    setPath("/dns-query");
    setPublicKey("");
    setProviderName("");
    setHashes("");
    setBootstrap("");
    setProperties({ dnssec: false, noLog: true, noFilter: true });
    setStampInput("");
    setResult(null);
    setError(null);
    setErrors({});
    resetCopyStatus();
  };

  /**
   * 2026-07-04 tools-wide overhaul (operator-approved), audit item #6 :
   * the Mode select card was duplicated verbatim across the encode and
   * decode branches ; this single renderer is now used by both. The
   * card className is a parameter because the decode branch carries its
   * own bottom margin while the encode branch relies on its column's
   * space-y-6 spacing.
   */
  const renderModeCard = (cardClassName) => (
    <div className={cardClassName}>
      <h2 className="text-lg font-semibold text-heading mb-2">Mode</h2>
      <p className="text-body mb-4">
        Select whether you want to generate (encode) a new DNS stamp from
        configuration parameters or decode an existing DNS stamp to view its
        configuration.
      </p>
      <select
        value={mode}
        onChange={(e) => {
          setMode(e.target.value);
          setResult(null);
          setError(null);
          setErrors({});
        }}
        className="input"
      >
        <option value="encode">Generate DNS Stamp (Encode)</option>
        <option value="decode">Decode DNS Stamp</option>
      </select>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 2026-07-04 tools-wide overhaul (operator-approved) : canonicalUrl
          added so search engines index the tool at its one true address. */}
      <SEO
        title="DNS Stamp Generator - Create and Decode DNS Stamps"
        description="Generate and decode DNS stamps for secure DNS configurations. Supports DNS-over-HTTPS, DNS-over-TLS, DNS-over-QUIC, DNSCrypt, and Oblivious DoH protocols."
        keywords="DNS stamp, DNS stamp generator, DNS-over-HTTPS, DNS-over-TLS, DNSCrypt, DoH, DoT, DoQ, Oblivious DoH, secure DNS"
        canonicalUrl="https://tools.trustico.com/dnsstamp"
      />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          DNS Stamp Generator
        </h1>
        <p className="text-body">
          Create and decode DNS stamps, the compact sdns:// addresses that
          describe a secure Domain Name System (DNS) resolver in a single
          shareable string. One stamp carries the server address, the protocol
          and any keys or hashes a client needs to connect. Plain DNS, DNSCrypt,
          DNS-over-HTTPS (DoH), DNS-over-TLS (DoT), DNS-over-QUIC (DoQ) and
          Oblivious DoH are supported, along with the DNSCrypt and Oblivious DoH
          relay types, following the IETF DNS stamps specification. Paste an
          existing stamp to see exactly what is inside it.
        </p>
      </div>

      <SecurityNotice />

      {mode === "encode" ? (
        <>
          <form onSubmit={handleEncodeSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Shared Mode card (audit item #6) - see renderModeCard */}
                {renderModeCard("card p-6")}

                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-heading mb-2">
                    Protocol Type
                  </h2>
                  <p className="text-body mb-4">
                    Select the DNS protocol type according to IETF
                    specifications. Each protocol provides different security
                    and performance characteristics for encrypted DNS queries.
                    The default port for each protocol is shown and will be used
                    if you don&apos;t specify a port number.
                  </p>
                  <select
                    value={protocol}
                    onChange={(e) => setProtocol(e.target.value)}
                    className="input"
                  >
                    {/* Audit item #6 : space before the colon per the
                        Trustico® prose convention. */}
                    {protocolOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}{" "}
                        {opt.port
                          ? `(Default Port : ${opt.port})`
                          : "(Port Required)"}
                      </option>
                    ))}
                  </select>
                </div>

                {showPropertiesField && (
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold text-heading mb-2">
                      Security and Privacy Properties
                    </h2>
                    <p className="text-body mb-4">
                      Configure the privacy and security properties for this DNS
                      resolver according to IETF DNS Stamps specification. These
                      properties are encoded as a 64-bit little-endian integer
                      with specific bit flags indicating the capabilities and
                      privacy policy of the DNS server.
                    </p>
                    <div className="space-y-6">
                      <div>
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={properties.dnssec}
                            onChange={(e) =>
                              setProperties((prev) => ({
                                ...prev,
                                dnssec: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm mt-1"
                          />
                          <div className="ml-3">
                            <span className="font-medium text-heading">
                              DNSSEC Validation
                            </span>
                            <p className="text-body mt-2">
                              When enabled, this property indicates that the DNS
                              resolver performs DNSSEC (Domain Name System
                              Security Extensions) validation on all DNS
                              responses. DNSSEC provides cryptographic
                              authentication of DNS data, ensuring that the DNS
                              information you receive has not been tampered with
                              during transmission.
                            </p>
                            <p className="text-body mt-3">
                              The resolver validates digital signatures on DNS
                              records against public keys stored in the DNS
                              hierarchy. This protects against DNS spoofing
                              attacks and cache poisoning. According to the IETF
                              specification, this property is encoded as Bit 0
                              in the properties field.
                            </p>
                            <p className="text-body mt-3">
                              You should enable DNSSEC validation if you require
                              strong assurance that DNS responses are authentic
                              and have not been modified by attackers. Note that
                              some domains do not support DNSSEC, in which case
                              the resolver will still return results but without
                              cryptographic validation.
                            </p>
                          </div>
                        </label>
                      </div>

                      <div>
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={properties.noLog}
                            onChange={(e) =>
                              setProperties((prev) => ({
                                ...prev,
                                noLog: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm mt-1"
                          />
                          <div className="ml-3">
                            <span className="font-medium text-heading">
                              No Query Logging
                            </span>
                            <p className="text-body mt-2">
                              When enabled, this property indicates that the DNS
                              resolver does not permanently log DNS queries.
                              This is important for privacy as DNS queries
                              reveal which websites and services you access.
                              Resolvers that log queries can build a detailed
                              profile of your internet activity over time.
                            </p>
                            <p className="text-body mt-3">
                              According to the IETF specification, this property
                              is encoded as Bit 1 in the properties field. A
                              resolver claiming this property commits to not
                              storing DNS query logs beyond what is necessary
                              for immediate operational purposes (such as
                              temporary caching for performance).
                            </p>
                            <p className="text-body mt-3">
                              You should enable this property if privacy is a
                              concern and you want to minimize the data trail of
                              your DNS lookups. However, note that this is a
                              claim made by the resolver operator and cannot be
                              cryptographically verified. You must trust the
                              resolver operator to honor this commitment.
                            </p>
                          </div>
                        </label>
                      </div>

                      <div>
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={properties.noFilter}
                            onChange={(e) =>
                              setProperties((prev) => ({
                                ...prev,
                                noFilter: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm mt-1"
                          />
                          <div className="ml-3">
                            <span className="font-medium text-heading">
                              No Domain Filtering
                            </span>
                            <p className="text-body mt-2">
                              When enabled, this property indicates that the DNS
                              resolver does not filter or block any domain
                              names. Some DNS resolvers implement filtering to
                              block access to malware domains, phishing sites,
                              adult content, or other categories of websites
                              deemed inappropriate or dangerous.
                            </p>
                            <p className="text-body mt-3">
                              According to the IETF specification, this property
                              is encoded as Bit 2 in the properties field. A
                              resolver with this property set will resolve all
                              domain names without censorship or content
                              filtering, returning NXDOMAIN (domain does not
                              exist) only when the domain genuinely does not
                              exist in the DNS.
                            </p>
                            <p className="text-body mt-3">
                              You should enable this property if you want
                              unrestricted DNS resolution without any filtering.
                              Disable this property if you prefer a resolver
                              that provides protection against known malicious
                              domains. Filtering policies vary by resolver and
                              may include malware protection, ad blocking, or
                              parental controls.
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-heading mb-2">
                    Configuration Parameters
                  </h2>
                  <p className="text-body mb-4">
                    Enter the configuration details for your DNS resolver
                    according to IETF specifications. Required fields vary
                    depending on the protocol type you selected. Fields marked
                    with an asterisk (*) are required for the selected protocol.
                  </p>
                  <div className="space-y-4">
                    {showAddressField && (
                      <div>
                        {/* Audit item #15 : the asterisk previously hid for
                            the DNSCrypt Relay (0x81) protocol, but the relay
                            is the one protocol where the address (with an
                            explicit port) is MOST required - the field is
                            only rendered when the protocol needs it, so the
                            asterisk is now unconditional. */}
                        <label htmlFor="address" className="label">
                          IP Address *
                        </label>
                        <p className="text-body mb-3">
                          The IP address of the DNS resolver. This can be an
                          IPv4 address (such as 8.8.8.8) or an IPv6 address
                          (such as 2620:fe::fe).
                        </p>
                        {/* Audit item #6 : space before the colon per the
                            Trustico® prose convention. */}
                        <p className="text-body mb-3">
                          <strong>IMPORTANT - IPv6 Format :</strong> According
                          to IETF specifications, IPv6 addresses with port
                          numbers MUST be enclosed in square brackets. For
                          example : [2001:4860:4860::8888]:853 for Google DNS
                          over TLS. IPv6 addresses without ports may optionally
                          use brackets : [2620:fe::fe] or 2620:fe::fe.
                        </p>
                        <p className="text-body mb-2">
                          If you don&apos;t specify a port number, the default
                          port for the selected protocol will be used (
                          {selectedProtocol?.port || "N/A"}). For IPv4, use the
                          format 1.1.1.1:853 to specify a custom port. For IPv6,
                          you MUST use the bracket format :
                          [2606:4700:4700::1111]:853.
                        </p>
                        {/* maxLength 255 : the DNS Stamps wire format
                            length-prefixes this field with a single byte, so
                            the server rejects longer values (audit item #15). */}
                        <input
                          type="text"
                          id="address"
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                            clearFieldError("address");
                          }}
                          maxLength={255}
                          placeholder={
                            selectedProtocol?.value === "0x81"
                              ? "1.1.1.1:443 or [2606::1]:443"
                              : "9.9.9.9 or [2620:fe::fe]"
                          }
                          className="input"
                        />
                        {errors.address && (
                          <p className="error-text">{errors.address}</p>
                        )}
                      </div>
                    )}

                    {showHostnameField && (
                      <div>
                        <label htmlFor="hostname" className="label">
                          Hostname *
                        </label>
                        <p className="text-body mb-3">
                          The hostname is used for TLS Certificate verification
                          and HTTPS requests. This must be a valid fully
                          qualified domain name (FQDN) such as dns.example.com.
                          The hostname identifies the server and must match the
                          Common Name or Subject Alternative Name in the
                          server&apos;s TLS Certificate.
                        </p>
                        <p className="text-body mb-3">
                          According to IETF specifications, the hostname is
                          stored in Unicode form within the DNS stamp. The
                          hostname will be converted to ASCII (Punycode) format
                          when establishing the connection if it contains
                          international characters. Do not manually encode to
                          Punycode when creating the stamp.
                        </p>
                        <p className="text-body mb-2">
                          For DNS-over-HTTPS, this hostname is used in the HTTPS
                          Host header and for TLS Server Name Indication (SNI).
                          For DNS-over-TLS and DNS-over-QUIC, it&apos;s used for
                          TLS Certificate validation to prevent
                          man-in-the-middle attacks.
                        </p>
                        {/* maxLength 255 : single-byte length prefix in the
                            DNS Stamps wire format (audit item #15). */}
                        <input
                          type="text"
                          id="hostname"
                          value={hostname}
                          onChange={(e) => {
                            setHostname(e.target.value);
                            clearFieldError("hostname");
                          }}
                          maxLength={255}
                          placeholder="dns.example.com"
                          className="input"
                        />
                        {errors.hostname && (
                          <p className="error-text">{errors.hostname}</p>
                        )}
                      </div>
                    )}

                    {showPathField && (
                      <div>
                        <label htmlFor="path" className="label">
                          Path *
                        </label>
                        <p className="text-body mb-3">
                          The URL path for DNS queries. According to IETF
                          specifications, the path MUST start with a forward
                          slash (/). The default and most common path is
                          /dns-query which is the standard endpoint defined in
                          RFC 8484 for DNS-over-HTTPS.
                        </p>
                        <p className="text-body mb-3">
                          Some DNS providers may use alternative paths such as
                          /resolve or /dns. Consult your DNS provider&apos;s
                          documentation to determine the correct path. The path
                          is combined with the hostname to form the complete URL
                          (e.g., https://dns.example.com/dns-query).
                        </p>
                        <p className="text-body mb-2">
                          For Oblivious DoH (ODoH), the path typically points to
                          the ODoH gateway or target endpoint. The path may
                          include query parameters if required by the provider,
                          but most standard implementations use /dns-query
                          without parameters.
                        </p>
                        {/* maxLength 255 : single-byte length prefix in the
                            DNS Stamps wire format (audit item #15). */}
                        <input
                          type="text"
                          id="path"
                          value={path}
                          onChange={(e) => {
                            setPath(e.target.value);
                            clearFieldError("path");
                          }}
                          maxLength={255}
                          placeholder="/dns-query"
                          className="input"
                        />
                        {errors.path && (
                          <p className="error-text">{errors.path}</p>
                        )}
                      </div>
                    )}

                    {showDNSCryptFields && (
                      <>
                        <div>
                          <label htmlFor="publicKey" className="label">
                            Public Key * (Ed25519, Hexadecimal)
                          </label>
                          <p className="text-body mb-3">
                            The Ed25519 public key of the DNSCrypt resolver in
                            hexadecimal format. According to IETF
                            specifications, this MUST be exactly 64 hexadecimal
                            characters representing 32 bytes of binary data. The
                            public key is used to verify the authenticity of the
                            DNS server and establish encrypted communications.
                          </p>
                          <p className="text-body mb-3">
                            DNSCrypt uses the Ed25519 elliptic curve signature
                            algorithm for authentication. The public key should
                            be obtained from your DNSCrypt provider&apos;s
                            official documentation or configuration files. Any
                            whitespace or formatting characters will be removed
                            during processing, but the key must still represent
                            exactly 32 bytes.
                          </p>
                          <p className="text-body mb-2">
                            Example valid format :
                            a1b2c3d4e5f67890abcdef1234567890abcdef1234567890abcdef1234567890
                            (64 hex characters). Do not include any prefixes
                            like &quot;0x&quot; or separators. The key is
                            case-insensitive.
                          </p>
                          {/* maxLength 128 : 64 hex characters plus room for
                              customer-pasted separators (colons, spaces),
                              which are stripped before the 32-byte check
                              (audit item #15). */}
                          <input
                            type="text"
                            id="publicKey"
                            value={publicKey}
                            onChange={(e) => {
                              setPublicKey(e.target.value);
                              clearFieldError("publicKey");
                            }}
                            maxLength={128}
                            placeholder="a1b2c3d4e5f678901234567890abcdef..."
                            className="input font-mono"
                          />
                          {errors.publicKey && (
                            <p className="error-text">{errors.publicKey}</p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="providerName" className="label">
                            Provider Name *
                          </label>
                          <p className="text-body mb-3">
                            The DNSCrypt provider name identifies the specific
                            DNSCrypt Certificate to use for communication with
                            the resolver. This is typically in the format
                            2.dnscrypt-cert.example.com where &quot;2&quot;
                            indicates the DNSCrypt protocol version.
                          </p>
                          <p className="text-body mb-3">
                            According to IETF specifications, the provider name
                            must be a valid DNS name without a trailing period.
                            The resolver uses this name to fetch the DNSCrypt
                            Certificate which contains the public key and other
                            parameters needed to establish an encrypted
                            connection.
                          </p>
                          <p className="text-body mb-2">
                            The provider name should be obtained from your
                            DNSCrypt provider&apos;s official documentation. It
                            must match exactly as configured on the server.
                            Common formats include 2.dnscrypt-cert.provider.com
                            or similar variations depending on the provider.
                          </p>
                          {/* maxLength 255 : single-byte length prefix in
                              the DNS Stamps wire format (audit item #15). */}
                          <input
                            type="text"
                            id="providerName"
                            value={providerName}
                            onChange={(e) => {
                              setProviderName(e.target.value);
                              clearFieldError("providerName");
                            }}
                            maxLength={255}
                            placeholder="2.dnscrypt-cert.example.com"
                            className="input"
                          />
                          {errors.providerName && (
                            <p className="error-text">{errors.providerName}</p>
                          )}
                        </div>
                      </>
                    )}

                    {showHashesField && (
                      <div>
                        <label htmlFor="hashes" className="label">
                          TLS Certificate Hashes (Optional, SHA-256)
                        </label>
                        {/* 2026-07-04 tools-wide overhaul (operator-approved),
                            audit item #6 : bare lowercase "certificate"
                            recased to "TLS Certificate" per the Trustico®
                            protected-term rule, and the possessive
                            "Certificate's DER-encoded form" reworded because
                            possessives on protected terms are not permitted. */}
                        <p className="text-body mb-3">
                          SHA-256 TLS Certificate hashes for TLS Certificate
                          pinning. TLS Certificate pinning provides additional
                          security by ensuring the DNS server presents an
                          expected TLS Certificate, protecting against
                          compromised Certificate Authorities and
                          man-in-the-middle attacks.
                        </p>
                        <p className="text-body mb-3">
                          According to IETF specifications, each hash MUST be
                          exactly 64 hexadecimal characters representing 32
                          bytes of SHA-256 digest data. Enter one hash per line
                          if you want to pin multiple TLS Certificates (useful
                          during TLS Certificate rotation). At least one hash
                          must match a TLS Certificate in the chain served by
                          the server for the connection to succeed.
                        </p>
                        <p className="text-body mb-2">
                          To obtain the hash, compute SHA-256 of the DER-encoded
                          form of the TLS Certificate. Most DNS providers
                          publish their TLS Certificate hashes in their
                          documentation. TLS Certificate pinning is optional but
                          recommended for high-security deployments. Leave blank
                          to skip pinning and rely on standard Certificate
                          Authority validation.
                        </p>
                        {/* maxLength 2048 : mirrors the sibling DCVChecker
                            expectedValue cap ; roomy enough for ~30 pinned
                            hashes at 65 characters per line (audit item #15). */}
                        <textarea
                          id="hashes"
                          value={hashes}
                          onChange={(e) => {
                            setHashes(e.target.value);
                            clearFieldError("hashes");
                          }}
                          maxLength={2048}
                          placeholder="a1b2c3d4e5f67890abcdef1234567890abcdef1234567890abcdef1234567890&#10;e5f6g7h8i9j0..."
                          rows={3}
                          className="textarea font-mono"
                        />
                        {errors.hashes && (
                          <p className="error-text">{errors.hashes}</p>
                        )}
                      </div>
                    )}

                    {showBootstrapField && (
                      <div>
                        <label htmlFor="bootstrap" className="label">
                          Bootstrap Resolver IP Addresses (Optional)
                        </label>
                        <p className="text-body mb-3">
                          Bootstrap resolver IP addresses are used for initial
                          hostname resolution before establishing the encrypted
                          DNS connection. Enter one IP address per line (IPv4 or
                          IPv6). Bootstrap resolvers help solve the
                          chicken-and-egg problem of using DNS to resolve the
                          hostname of a DNS server.
                        </p>
                        <p className="text-body mb-3">
                          When you specify a hostname for the encrypted DNS
                          resolver (e.g., dns.example.com), your system needs to
                          resolve that hostname to an IP address. Bootstrap
                          resolvers are used for this initial lookup. They can
                          be public DNS servers like 1.1.1.1 or 8.8.8.8.
                        </p>
                        <p className="text-body mb-2">
                          Bootstrap resolvers are optional if you specify the IP
                          address directly instead of a hostname, or if the
                          hostname is already cached. According to IETF
                          specifications, bootstrap addresses are stored as a
                          variable-length array in the DNS stamp. Each address
                          must be a valid IPv4 or IPv6 address.
                        </p>
                        {/* maxLength 1024 : each bootstrap entry is capped
                            at 127 bytes by the wire format's VLP encoding,
                            and realistic lists hold a handful of resolver
                            addresses (audit item #15). */}
                        <textarea
                          id="bootstrap"
                          value={bootstrap}
                          onChange={(e) => {
                            setBootstrap(e.target.value);
                            clearFieldError("bootstrap");
                          }}
                          maxLength={1024}
                          placeholder="1.1.1.1&#10;8.8.8.8&#10;2606:4700:4700::1111"
                          rows={3}
                          className="textarea"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              {/* Three-across only from lg (see the identical row on
                  CSRGenerator) : three sm-min-width buttons plus gaps
                  exceed a 640-800px viewport and would overlap. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-4xl">
                {/* 2026-07-04 operator consistency directive : the
                    primary action is disabled until every field the
                    SELECTED protocol requires is non-empty (the
                    show*Field flags mirror the wire-format rules the
                    validators enforce). validateEncodeForm keeps its
                    required-field checks as the direct-form-submission
                    backstop and still supplies the on-click FORMAT
                    validation (explicit relay port, hex key length,
                    leading slash, hash lengths). */}
                <ActionButton
                  type="submit"
                  variant="primary"
                  disabled={
                    loading ||
                    (showAddressField && !address.trim()) ||
                    (showHostnameField && !hostname.trim()) ||
                    (showPathField && !path.trim()) ||
                    (showDNSCryptFields &&
                      (!publicKey.trim() || !providerName.trim()))
                  }
                  loading={loading}
                  loadingText="Generating..."
                  icon={FingerPrintIcon}
                >
                  Generate DNS Stamp
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={handleReset}
                  variant="outline"
                  icon={XMarkIcon}
                >
                  {/* 2026-07-04 operator consistency directive : reset
                      buttons across every tool page say "Reset" (the
                      previous label was "Clear Data"). */}
                  Reset
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={fillExample}
                  variant="outline"
                  icon={DocumentTextIcon}
                >
                  Fill Example Data
                </ActionButton>
              </div>
            </div>
          </form>
        </>
      ) : (
        <>
          {/* Shared Mode card (audit item #6) - see renderModeCard */}
          {renderModeCard("card p-6 mb-8")}

          <form onSubmit={handleDecodeSubmit} className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              DNS Stamp
            </h2>
            <p className="text-body mb-4">
              Paste a DNS stamp below to decode and view its configuration
              parameters. DNS stamps start with the prefix sdns:// followed by
              Base64URL-encoded binary data containing the protocol type, server
              address, hostname, and security properties according to IETF
              specifications.
            </p>
            {/* Audit item #15 : the HTML `required` attribute was replaced
                by the state-driven validateDecodeForm check so validation
                feedback renders in the standard error-text style instead
                of the browser-native bubble. maxLength 2048 comfortably
                covers real stamps (the wire format's per-field 255-byte
                caps keep encoded stamps far shorter). */}
            <textarea
              value={stampInput}
              onChange={(e) => {
                setStampInput(e.target.value);
                clearFieldError("stampInput");
              }}
              maxLength={2048}
              placeholder="sdns://AgcAAAAAAAAAAAAPZG5zLnF1YWQ5Lm5ldAovZG5zLXF1ZXJ5"
              rows={4}
              className="textarea font-mono mb-4"
            />
            {errors.stampInput && (
              <p className="error-text mb-4">{errors.stampInput}</p>
            )}

            <div className="flex justify-center">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                {/* 2026-07-04 operator consistency directive : the
                    decode action is disabled until the stamp input is
                    non-empty ; validateDecodeForm keeps the sdns://
                    prefix check as on-click format validation and as
                    the direct-form-submission backstop. */}
                <ActionButton
                  type="submit"
                  variant="primary"
                  disabled={loading || !stampInput.trim()}
                  loading={loading}
                  loadingText="Decoding..."
                  icon={FingerPrintIcon}
                >
                  Decode DNS Stamp
                </ActionButton>
                <ActionButton
                  type="button"
                  onClick={handleReset}
                  variant="outline"
                  icon={XMarkIcon}
                >
                  {/* Reset label per the 2026-07-04 operator
                      consistency directive (see the encode-branch
                      Reset button above). */}
                  Reset
                </ActionButton>
              </div>
            </div>
          </form>
        </>
      )}

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && result.type === "encode" && (
        <div ref={resultsRef} className="mt-8 animate-fade-in">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Generated DNS Stamp
            </h2>
            <p className="text-body mb-4">
              Your DNS stamp has been generated successfully. Copy this stamp
              and use it in your DNS client configuration. The stamp encodes all
              the parameters you specified according to IETF DNS Stamps
              specifications.
            </p>
            <textarea
              value={result.data.stamp}
              readOnly
              className="textarea certificate-textarea mb-4"
              rows={4}
            />
            <div className="flex justify-center">
              {/* 2026-08-07 download/copy consistency pass
                  (operator-directed) : the copy button gains a
                  Download sibling in the standard centered two-column
                  action row (the CSRGenerator results-card idiom). */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                {/* Audit item #12 : transient "Copied!" feedback via the
                    copyStatus state, mirroring CSRGenerator. */}
                <ActionButton
                  onClick={() => handleCopyStamp(result.data.stamp)}
                  variant="outline"
                  icon={ClipboardDocumentIcon}
                >
                  {copyStatus.stamp ? "Copied!" : "Copy DNS Stamp"}
                </ActionButton>
                <ActionButton
                  onClick={() => handleDownloadStamp(result.data.stamp)}
                  variant="outline"
                  icon={ArrowDownTrayIcon}
                >
                  Download DNS Stamp
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {result && result.type === "decode" && (
        <div ref={resultsRef} className="mt-8 animate-fade-in space-y-6">
          {/* Protocol Information */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Protocol Information
            </h2>
            <p className="text-body mb-4">
              The DNS stamp encodes a {result.data.decoded.protocolName}{" "}
              resolver configuration. This protocol determines how DNS queries
              are encrypted and transmitted.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Protocol
                </span>
                <p className="text-heading mt-1 font-mono">
                  {result.data.decoded.protocol}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Protocol Name
                </span>
                <p className="text-heading mt-1">
                  {result.data.decoded.protocolName}
                </p>
              </div>
            </div>
          </div>

          {/* Properties */}
          {result.data.decoded.properties && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Resolver Properties
              </h2>
              <p className="text-body mb-4">
                These properties indicate the security and privacy features
                supported by the DNS resolver.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    DNSSEC
                  </span>
                  <p className="text-heading mt-1">
                    {result.data.decoded.properties.dnssec
                      ? "✓ Enabled"
                      : "✗ Disabled"}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    No Logging
                  </span>
                  <p className="text-heading mt-1">
                    {result.data.decoded.properties.noLog
                      ? "✓ Enabled"
                      : "✗ Disabled"}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    No Filtering
                  </span>
                  <p className="text-heading mt-1">
                    {result.data.decoded.properties.noFilter
                      ? "✓ Enabled"
                      : "✗ Disabled"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Connection Information */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Connection Information
            </h2>
            <p className="text-body mb-4">
              Network address and hostname information for connecting to the DNS
              resolver.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {result.data.decoded.address && (
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      IP Address
                    </span>
                    <CopyValueButton
                      label="IP Address"
                      value={result.data.decoded.address}
                      fieldKey="decoded-address"
                      copyStatus={copyStatus}
                      onCopy={handleCopy}
                    />
                  </div>
                  <p className="text-heading mt-1 font-mono">
                    {result.data.decoded.address}
                  </p>
                </div>
              )}
              {result.data.decoded.hostname && (
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      Hostname
                    </span>
                    <CopyValueButton
                      label="Hostname"
                      value={result.data.decoded.hostname}
                      fieldKey="decoded-hostname"
                      copyStatus={copyStatus}
                      onCopy={handleCopy}
                    />
                  </div>
                  <p className="text-heading mt-1 font-mono">
                    {result.data.decoded.hostname}
                  </p>
                </div>
              )}
              {result.data.decoded.path && (
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      Path
                    </span>
                    <CopyValueButton
                      label="Path"
                      value={result.data.decoded.path}
                      fieldKey="decoded-path"
                      copyStatus={copyStatus}
                      onCopy={handleCopy}
                    />
                  </div>
                  <p className="text-heading mt-1 font-mono">
                    {result.data.decoded.path}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* DNSCrypt Information */}
          {(result.data.decoded.publicKey ||
            result.data.decoded.providerName) && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                DNSCrypt Configuration
              </h2>
              <p className="text-body mb-4">
                DNSCrypt-specific parameters for encrypted DNS resolution using
                the DNSCrypt protocol.
              </p>
              <div className="space-y-4">
                {result.data.decoded.publicKey && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-medium text-muted uppercase tracking-wider">
                        Public Key (Ed25519)
                      </span>
                      <CopyValueButton
                        label="Public Key (Ed25519)"
                        value={result.data.decoded.publicKey}
                        fieldKey="decoded-publicKey"
                        copyStatus={copyStatus}
                        onCopy={handleCopy}
                      />
                    </div>
                    <p className="text-heading mt-1 font-mono break-all">
                      {result.data.decoded.publicKey}
                    </p>
                  </div>
                )}
                {result.data.decoded.providerName && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-medium text-muted uppercase tracking-wider">
                        Provider Name
                      </span>
                      <CopyValueButton
                        label="Provider Name"
                        value={result.data.decoded.providerName}
                        fieldKey="decoded-providerName"
                        copyStatus={copyStatus}
                        onCopy={handleCopy}
                      />
                    </div>
                    <p className="text-heading mt-1 font-mono">
                      {result.data.decoded.providerName}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Certificate Hashes */}
          {result.data.decoded.hashes &&
            result.data.decoded.hashes.length > 0 && (
              <div className="card p-6">
                {/* Audit item #6 : heading and body recased to the protected
                    "TLS Certificate" term (never the bare lowercase word). */}
                <h2 className="text-lg font-semibold text-heading mb-2">
                  TLS Certificate Pinning Hashes
                </h2>
                <p className="text-body mb-4">
                  SHA-256 hashes of pinned TLS Certificates. The resolver must
                  present a TLS Certificate matching one of these hashes.
                </p>
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      {result.data.decoded.hashes.length} Hash
                      {result.data.decoded.hashes.length !== 1 ? "es" : ""}
                    </span>
                    {/* One button for the whole list (newline-joined),
                        the shape resolver configs consume ; per-hash
                        buttons would add a control per row for no
                        gain. */}
                    <CopyValueButton
                      label="Pinning Hashes"
                      value={result.data.decoded.hashes.join("\n")}
                      fieldKey="decoded-hashes"
                      copyStatus={copyStatus}
                      onCopy={handleCopy}
                    />
                  </div>
                  <div className="mt-2 space-y-2">
                    {result.data.decoded.hashes.map((hash, index) => (
                      <p
                        key={index}
                        className="text-heading font-mono break-all"
                      >
                        {hash}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

          {/* Bootstrap IPs */}
          {result.data.decoded.bootstrap &&
            result.data.decoded.bootstrap.length > 0 && (
              <div className="card p-6">
                <h2 className="text-lg font-semibold text-heading mb-2">
                  Bootstrap Resolvers
                </h2>
                <p className="text-body mb-4">
                  IP addresses of resolvers to use for initial hostname
                  resolution. These are used to resolve the resolver&apos;s
                  hostname if needed.
                </p>
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      {result.data.decoded.bootstrap.length} Bootstrap IP
                      {result.data.decoded.bootstrap.length !== 1 ? "s" : ""}
                    </span>
                    {/* One button for the whole list, matching the
                        pinning-hashes treatment above. */}
                    <CopyValueButton
                      label="Bootstrap Resolvers"
                      value={result.data.decoded.bootstrap.join("\n")}
                      fieldKey="decoded-bootstrap"
                      copyStatus={copyStatus}
                      onCopy={handleCopy}
                    />
                  </div>
                  <div className="mt-2 space-y-1">
                    {result.data.decoded.bootstrap.map((ip, index) => (
                      <p key={index} className="text-heading font-mono">
                        {ip}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

          {/* Centered single-action row RE-SAVING THE PASTED STAMP
              under a standard filename named from the decoded resolver
              (see the capture comment in handleDecodeSubmit). Stated
              plainly : the customer already holds this string - the
              value added is the standardized, resolver-named file,
              exactly the deal CSRDecoder's Download CSR offers for a
              pasted CSR. The decoded DETAILS themselves are a
              diagnostic view with per-field copy, not a downloadable
              artifact (operator-reviewable design choice). */}
          <div className="flex justify-center">
            <ActionButton
              onClick={() => handleDownloadStamp(result.stamp)}
              variant="outline"
              icon={ArrowDownTrayIcon}
            >
              Download DNS Stamp
            </ActionButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default DNSStampGenerator;
